import {
  Module,
  VuexModule,
  Mutation,
  Action,
  config
} from "vuex-module-decorators";
import {
  OnboardingData,
  OnboardingWebsiteDetails,
  Sitemap
} from "../../subapps/onboarding/types";
import defensiveCopy from "@/util/defensive-copy";
import {
  generatePalette,
  randomColor
} from "../../subapps/onboarding/lib/color";

import { BASE_BREAKPOINT_ID } from "@/lib/breakpoints/breakpoints";
import { generateNeutralsPalette } from "@/lib/singularity/brand/colors/palettes";

config.rawError = true;

const MAX_SECONDARY_PALETTES = 3;
const BAREBONES_DESIGN_PROVIDER_NAME = "Barebones";

@Module({ name: "onboarding", namespaced: true })
export default class OnboardingModule extends VuexModule {
  onboardingData: OnboardingData = {
    websiteDetails: {
      websiteName: "",
      websiteDescription: "",
      websiteCategory: [],
      websiteGoals: [],
      websiteWritingStyle: []
    },
    type: "barebones",
    websiteUI: {
      colorPalettes: {
        neutral: null,
        primary: null,
        secondary: []
      },
      typography: {
        headings: null,
        body: null
      },
      buttons: {
        radius: "md"
      },
      images: {
        radius: "md"
      }
    },
    generatedPages: {
      homepage: null,
      pages: [],
      toRemove: { homepageSectionIds: null, pageIds: null }
    },
    previewBreakpointId: BASE_BREAKPOINT_ID,
    designLibrary: {
      selectedProvider: null,
      data: {
        designProviders: [],
        globalSettingsLastImportedFromUrl: null,
        designPresetsLastImportedFromUrl: null,
        variablesLastImportedFromUrl: null,
        oxySelectorsLastImportedFromUrl: null,
        fullSiteLastImportedFromUrl: null,
        isGlobalSettingsEnabled: true,
        isDesignPresetsEnabled: true,
        isVariablesEnabled: false,
        isOxySelectorsEnabled: false,
        isPopupsEnabled: true
      }
    },
    sitemap: null,
    currentTree: null,
    activeElementId: null
  };

  untouchedOnboardingData: OnboardingData = defensiveCopy(this.onboardingData);

  get websiteDetails(): OnboardingWebsiteDetails {
    return this.onboardingData.websiteDetails;
  }

  get websiteUI() {
    return this.onboardingData.websiteUI;
  }

  get onboardingType() {
    return this.onboardingData.type;
  }

  get isSecondaryPalettesLimitReached() {
    return (
      this.onboardingData.websiteUI.colorPalettes.secondary.length >=
      MAX_SECONDARY_PALETTES
    );
  }

  get generatedPages() {
    return this.onboardingData.generatedPages;
  }

  get isHomepageCreated() {
    return this.onboardingData.generatedPages?.homepage?.postId !== undefined;
  }

  get previewBreakpointId() {
    return this.onboardingData.previewBreakpointId;
  }

  get homepageSectionIdsToRemove() {
    return this.onboardingData.generatedPages?.toRemove.homepageSectionIds;
  }

  get pageIdsToRemove() {
    return this.onboardingData.generatedPages?.toRemove.pageIds;
  }

  get designLibrary() {
    return this.onboardingData.designLibrary;
  }

  get sitemap(): Sitemap | null {
    return this.onboardingData.sitemap;
  }

  get currentTree() {
    return this.onboardingData.currentTree;
  }

  get activeElementId() {
    return this.onboardingData.activeElementId;
  }

  // Mutations

  @Mutation
  setOnboardingData(data: Partial<OnboardingData>) {
    this.onboardingData = {
      ...this.onboardingData,
      ...data
    };
  }

  @Mutation
  setWebsiteDetails(details: Partial<OnboardingWebsiteDetails>) {
    this.onboardingData.websiteDetails = {
      ...this.onboardingData.websiteDetails,
      ...details
    };
  }

  @Mutation
  setOnboardingType(type: OnboardingData["type"]) {
    this.onboardingData.type = type;
  }

  @Mutation
  setWebsiteUI(ui: Partial<OnboardingData["websiteUI"]>) {
    this.onboardingData.websiteUI = {
      ...this.onboardingData.websiteUI,
      ...ui
    };
  }

  @Mutation
  setTypography(
    typography: Partial<OnboardingData["websiteUI"]["typography"]>
  ) {
    this.onboardingData.websiteUI.typography = {
      ...this.onboardingData.websiteUI.typography,
      ...typography
    };
  }

  @Mutation
  markCurrentOnboardingDataAsUntouched() {
    this.untouchedOnboardingData = defensiveCopy(this.onboardingData);
  }

  @Mutation
  setNeutralsPalette(
    palette: OnboardingData["websiteUI"]["colorPalettes"]["neutral"]
  ) {
    this.onboardingData.websiteUI.colorPalettes.neutral = palette;
  }

  @Mutation
  setPrimaryPalette(
    palette: OnboardingData["websiteUI"]["colorPalettes"]["primary"]
  ) {
    this.onboardingData.websiteUI.colorPalettes.primary = palette;
  }

  @Mutation
  setSecondaryPalettes(
    palettes: OnboardingData["websiteUI"]["colorPalettes"]["secondary"]
  ) {
    this.onboardingData.websiteUI.colorPalettes.secondary = palettes;
  }

  @Mutation
  addSecondaryPalette(
    palette: OnboardingData["websiteUI"]["colorPalettes"]["secondary"][number]
  ) {
    this.onboardingData.websiteUI.colorPalettes.secondary.push(palette);
  }

  @Mutation
  setSecondaryPalette({
    index,
    palette
  }: {
    index: number;
    palette: OnboardingData["websiteUI"]["colorPalettes"]["secondary"][number];
  }) {
    this.onboardingData.websiteUI.colorPalettes.secondary.splice(
      index,
      1,
      palette
    );
  }

  @Mutation
  removeSecondaryPalette(index: number) {
    this.onboardingData.websiteUI.colorPalettes.secondary.splice(index, 1);
  }

  @Mutation
  setGeneratedPages(generatedPages: OnboardingData["generatedPages"]) {
    this.onboardingData.generatedPages = generatedPages;
  }

  @Mutation
  setGeneratedPage({
    homepage,
    pages,

    toRemove
  }: {
    homepage: NonNullable<OnboardingData["generatedPages"]>["homepage"];
    pages: NonNullable<OnboardingData["generatedPages"]>["pages"];

    toRemove: NonNullable<OnboardingData["generatedPages"]>["toRemove"];
  }) {
    this.onboardingData.generatedPages = {
      homepage,
      pages,

      toRemove
    };
  }

  @Mutation
  setGeneratedPageHomepage(
    homepage: OnboardingData["generatedPages"]["homepage"]
  ) {
    this.onboardingData.generatedPages.homepage = homepage;
  }

  @Mutation
  setGeneratedPagePages(
    pages: NonNullable<OnboardingData["generatedPages"]>["pages"]
  ) {
    if (this.onboardingData.generatedPages) {
      this.onboardingData.generatedPages.pages = pages;
    }
  }

  @Mutation
  addToGeneratedPages(
    page: NonNullable<OnboardingData["generatedPages"]>["pages"][number]
  ) {
    if (this.onboardingData.generatedPages) {
      this.onboardingData.generatedPages.pages.push(page);
    }
  }

  @Mutation
  removeFromGeneratedPages(postId: number) {
    if (this.onboardingData.generatedPages) {
      this.onboardingData.generatedPages.pages =
        this.onboardingData.generatedPages.pages.filter(
          page => page.postId !== postId
        );
    }
  }

  @Mutation
  setButtons(buttons: Partial<OnboardingData["websiteUI"]["buttons"]>) {
    this.onboardingData.websiteUI.buttons = {
      ...this.onboardingData.websiteUI.buttons,
      ...buttons
    };
  }
  @Mutation
  setImages(images: Partial<OnboardingData["websiteUI"]["images"]>) {
    this.onboardingData.websiteUI.images = {
      ...this.onboardingData.websiteUI.images,
      ...images
    };
  }

  @Mutation
  setPreviewBreakpointId(breakpointId: string) {
    this.onboardingData.previewBreakpointId = breakpointId;
  }

  @Mutation
  setHomepageSectionIdsToRemove(homepageSectionIds: number[] | null) {
    this.onboardingData.generatedPages.toRemove.homepageSectionIds =
      homepageSectionIds;
  }

  @Mutation
  setPageIdsToRemove(pageIds: number[] | null) {
    this.onboardingData.generatedPages.toRemove.pageIds = pageIds;
  }

  @Mutation
  setDesignLibraryData(data: OnboardingData["designLibrary"]["data"]) {
    this.onboardingData.designLibrary.data = data;
  }

  @Mutation
  setDesignLibrarySelectedProvider(
    selectedProvider: OnboardingData["designLibrary"]["selectedProvider"]
  ) {
    this.onboardingData.designLibrary.selectedProvider = selectedProvider;
  }

  @Mutation
  setSitemap(sitemap: Sitemap | null) {
    this.onboardingData.sitemap = sitemap;
  }

  @Mutation
  removePageFromSitemap(pageIndex: number) {
    if (this.onboardingData.sitemap) {
      this.onboardingData.sitemap.splice(pageIndex, 1);
    }
  }

  @Mutation
  setCurrentTree(tree: OnboardingData["currentTree"]) {
    this.onboardingData.currentTree = tree;
  }

  @Mutation
  setActiveElementId(activeElementId: OnboardingData["activeElementId"]) {
    this.onboardingData.activeElementId = activeElementId;
  }

  // Actions
  @Action
  updateActiveElementId(activeElementId: OnboardingData["activeElementId"]) {
    this.setActiveElementId(activeElementId);
  }

  @Action
  updateCurrentTree(tree: OnboardingData["currentTree"]) {
    this.setCurrentTree(tree);
  }

  @Action
  removePageFromSitemapAction(pageIndex: number) {
    this.removePageFromSitemap(pageIndex);
  }

  @Action
  updateSitemap(sitemap: Sitemap | null) {
    this.setSitemap(sitemap);
  }

  @Action
  setBarebonesDesignProvider() {
    const designProvider =
      this.onboardingData.designLibrary.data.designProviders.find(
        provider => provider.name === BAREBONES_DESIGN_PROVIDER_NAME
      );
    if (designProvider) {
      this.setDesignLibrarySelectedProvider(designProvider);
    }
  }

  @Action
  updateDesignLibrarySelectedProvider(
    selectedProvider: OnboardingData["designLibrary"]["selectedProvider"] | null
  ) {
    this.setDesignLibrarySelectedProvider(selectedProvider);
  }

  @Action
  updateDesignLibraryData(data: OnboardingData["designLibrary"]["data"]) {
    this.setDesignLibraryData(data);
  }

  @Action
  updateHomepageSectionIdsToRemove(homepageSectionIds: number[] | null) {
    this.setHomepageSectionIdsToRemove(homepageSectionIds);
  }

  @Action
  updatePageIdsToRemove(pageIds: number[] | null) {
    this.setPageIdsToRemove(pageIds);
  }

  @Action
  updatePreviewBreakpointId(breakpointId: string) {
    this.setPreviewBreakpointId(breakpointId);
  }

  @Action
  createPrimaryPalette() {
    const palette = generatePalette(randomColor(), true);
    this.setPrimaryPalette(palette);
    this.updateNeutralPalette(palette);
  }

  @Action
  updateNeutralsPalette(
    palette: OnboardingData["websiteUI"]["colorPalettes"]["neutral"]
  ) {
    this.setNeutralsPalette(palette);
  }

  @Action
  updatePrimaryPalette(
    palette: OnboardingData["websiteUI"]["colorPalettes"]["primary"]
  ) {
    this.setPrimaryPalette(palette);
    this.updateNeutralPalette(palette);
  }

  @Action
  updateSecondaryPalettes(
    palettes: OnboardingData["websiteUI"]["colorPalettes"]["secondary"]
  ) {
    this.setSecondaryPalettes(palettes);
  }

  @Action
  updateSecondaryPalette({
    index,
    palette
  }: {
    index: number;
    palette: OnboardingData["websiteUI"]["colorPalettes"]["secondary"][number];
  }) {
    this.setSecondaryPalette({ index, palette });
  }

  @Action
  addToSecondaryPalettes(
    palette?: OnboardingData["websiteUI"]["colorPalettes"]["secondary"][number]
  ) {
    if (!palette) {
      palette = generatePalette(randomColor());
    }
    this.addSecondaryPalette(palette);
  }

  @Action
  removeFromSecondaryPalettes(index: number) {
    this.removeSecondaryPalette(index);
  }

  @Action
  updateWebsiteDetails(details: Partial<OnboardingWebsiteDetails>) {
    this.setWebsiteDetails(details);
  }

  @Action
  updateOnboardingType(type: OnboardingData["type"]) {
    this.setOnboardingType(type);
  }

  @Action
  updateWebsiteUI(ui: Partial<OnboardingData["websiteUI"]>) {
    this.setWebsiteUI(ui);
  }

  @Action
  updateTypography(
    typography: Partial<OnboardingData["websiteUI"]["typography"]>
  ) {
    this.setTypography(typography);
  }

  @Action
  initializePalettes() {
    if (this.onboardingData.websiteUI.colorPalettes.primary) {
      return;
    }

    const primary = generatePalette(randomColor(), true);
    const secondary = [generatePalette(randomColor())];

    this.setPrimaryPalette(primary);
    this.setSecondaryPalettes(secondary);
    this.setNeutralsPalette(
      generateNeutralsPalette(String(primary.scale[900].value))
    );
  }

  @Action
  shufflePrimaryPalette() {
    if (this.onboardingData.websiteUI.colorPalettes.primary?.locked) {
      return;
    }
    const primary = generatePalette(randomColor(), true);
    this.setPrimaryPalette(primary);
    this.updateNeutralPalette(primary);
  }

  @Action
  shuffleSecondaryPalettes() {
    // Generate a new random color for each secondary palette, omitting locked palettes
    const secondary = this.websiteUI.colorPalettes.secondary.map(palette =>
      palette.locked ? palette : generatePalette(randomColor())
    );
    this.setSecondaryPalettes(secondary);
  }

  @Action
  shufflePalettes() {
    this.shufflePrimaryPalette();
    this.shuffleSecondaryPalettes();
  }

  @Action
  updateNeutralPalette(
    palette: OnboardingData["websiteUI"]["colorPalettes"]["neutral"]
  ) {
    const primary900 =
      palette?.scale[900].value ||
      this.onboardingData.websiteUI.colorPalettes.primary?.scale[900].value;

    if (!primary900) {
      return;
    }
    const neutralPalette = generateNeutralsPalette(String(primary900));
    this.setNeutralsPalette(neutralPalette);
  }

  @Action
  resetOnboardingData() {
    this.setOnboardingData(this.untouchedOnboardingData);
  }

  @Action
  resetOnboardingDataToDefault() {
    this.onboardingData = defensiveCopy(this.untouchedOnboardingData);
    this.onboardingData.type = "barebones";
    this.onboardingData.websiteUI.colorPalettes.neutral = null;
    this.onboardingData.websiteUI.colorPalettes.primary = null;
    this.onboardingData.websiteUI.colorPalettes.secondary = [];
  }

  @Action
  updateGeneratedPages(generatedPages: OnboardingData["generatedPages"]) {
    this.setGeneratedPages(generatedPages);
  }

  @Action
  updateGeneratedPage({
    homepage,
    pages,
    toRemove
  }: {
    homepage: NonNullable<OnboardingData["generatedPages"]>["homepage"];
    pages: NonNullable<OnboardingData["generatedPages"]>["pages"];
    toRemove: NonNullable<OnboardingData["generatedPages"]>["toRemove"];
  }) {
    this.setGeneratedPage({ homepage, pages, toRemove });
  }

  @Action
  updateGeneratedPageHomepage(
    homepage: NonNullable<OnboardingData["generatedPages"]>["homepage"]
  ) {
    this.setGeneratedPageHomepage(homepage);
  }

  @Action
  updateGeneratedPagePages(
    pages: NonNullable<OnboardingData["generatedPages"]>["pages"]
  ) {
    this.setGeneratedPagePages(pages);
  }

  @Action
  addToGeneratedPagesPage(
    page: NonNullable<OnboardingData["generatedPages"]>["pages"][number]
  ) {
    this.addToGeneratedPages(page);
  }

  @Action
  removeFromGeneratedPagesPage(postId: number) {
    this.removeFromGeneratedPages(postId);
  }

  @Action
  updateButtons(buttons: Partial<OnboardingData["websiteUI"]["buttons"]>) {
    this.setButtons(buttons);
  }
  @Action
  updateImages(images: Partial<OnboardingData["websiteUI"]["images"]>) {
    this.setImages(images);
  }
}
